/**
 * `containment.cycle` (§5.1 Blocking rules; `05-geography.md` §14; #207) — a
 * directed cycle in the `contained_in` containment graph **within a single
 * context**. A same-context loop (A geographically inside B inside A) is
 * geometrically impossible — there is no consistent coordinate frame — and so
 * breaks frame composition (`§5.3`) and the navigation tie-break (`§6`). Unlike
 * the disputed-parentage case (overlapping validity within a context, which is
 * itself canon-worthy and only *informational*), a cycle is structurally
 * corrupt, so the rule emits a `blocking_error`: the only severity the engine
 * refuses a write at (`advisory.ts`). The GM cannot dismiss-to-write — they must
 * edit the offending fact (close `valid_to`, change parent, or change context).
 *
 * **Same-context only.** Cross-context cycles are legal and must NOT flag — a
 * capital city geographically inside a nation that is politically seated in that
 * city is a real arrangement, not a paradox. The graph is therefore PARTITIONED
 * by context (`containmentContext`, defaulting to `geographic` when omitted):
 * edges only ever join two facts sharing a context, and each partition is
 * analysed independently.
 *
 * **`mythic` opt-out.** The in-fiction-paradox escape hatch (`§14`) — a pocket
 * dimension that contains its containing world — is authored in the `mythic`
 * context (or another {@link CYCLE_EXEMPT_CONTEXTS} member), where the
 * structural restriction is deliberately lifted. Within `geographic` the
 * restriction holds absolutely.
 *
 * **Validity-aware.** A Location that switches parents over time (`§2.2`) is two
 * `contained_in` facts with non-overlapping validity — they never coexist, so
 * they are not a cycle at any single instant. The rule sweeps the distinct
 * validity boundaries and only flags a loop whose edges are simultaneously live.
 *
 * Owner-scoped, mirroring the other cross-fact rules: only CANON containment
 * facts build the canon graph. A belief that A is inside B lives in a believer's
 * own layer and never forms a canon cycle.
 *
 * Containment is detected structurally — a predicate whose declaration's
 * `value_kind` includes `'containment'` — so the rule stays pack-agnostic and
 * English-free (no hard-coded `contained_in` id; the `mythic` opt-out is the one
 * vocabulary constant, and it is a free-string *value*, not declared vocab).
 * Reuses the shared {@link analyzeDirectedGraph} cycle detector the `causal.cycle`
 * rule established (DRY).
 */
import {
  analyzeDirectedGraph,
  containmentContext,
  containmentParent,
  currentFactValue,
  type DirectedEdge,
  type EntityState,
  type FactValidity,
} from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { forEachFact } from './helpers.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const CONTAINMENT_CYCLE_RULE = 'containment.cycle';

/**
 * Contexts where the same-context cycle restriction is opted OUT (`§14`). The
 * `mythic` context is the documented escape hatch for in-fiction paradoxes (a
 * Möbius city, a pocket dimension that contains its world). Membership is a
 * free-string *value* on the containment, not declared vocab — so this is a
 * plain value comparison, not an opaque-id lookup.
 */
export const CYCLE_EXEMPT_CONTEXTS: ReadonlySet<string> = new Set(['mythic']);

/** A containment edge `child → parent` in one context, with its validity + provenance. */
interface ContainmentEdge {
  child: string;
  parent: string;
  validity: FactValidity | null;
  /** `factRef(child, factKey)` — points a detected loop back at the offending fact. */
  via: string;
}

/** Lower-bound `from` of a validity window (`-∞` when unbounded / timeless). */
function lowerBound(v: FactValidity | null): number {
  return v?.from ?? Number.NEGATIVE_INFINITY;
}

/**
 * Is an edge LIVE at instant `t`? Half-open `[from, to)` (ADR-0005 §4): `from`
 * inclusive, `to` exclusive; absent bounds are unbounded; a timeless edge
 * (`validity === null`) is always live.
 */
function liveAt(v: FactValidity | null, t: number): boolean {
  if (v === null) return true;
  const from = v.from ?? Number.NEGATIVE_INFINITY;
  const to = v.to ?? Number.POSITIVE_INFINITY;
  return from <= t && t < to;
}

export function containmentCycle(ctx: AuditContext): RawAdvisory[] {
  // Bucket every live CANON containment edge by its context. Cross-context
  // edges never share a bucket, so a loop spanning two contexts is structurally
  // impossible to detect (which is the point — those are legal).
  const byContext = new Map<string, ContainmentEdge[]>();

  forEachFact(ctx.entities, ({ entity, factKey, predicate, fact }) => {
    if (fact.owner.kind !== 'canon') return; // canon-only graph
    const decl = ctx.registry.lookup(predicate);
    if (!decl || !decl.value_kind.includes('containment')) return;

    const value = currentFactValue(fact);
    const parent = containmentParent(value);
    if (parent === null) return; // malformed containment → value_kind_mismatch's job
    const target = ctx.entities[parent] as EntityState | undefined;
    if (target === undefined || target.soft_deleted) return; // dangling → ref.dangling's job

    const context = containmentContext(value);
    if (CYCLE_EXEMPT_CONTEXTS.has(context)) return; // mythic opt-out (§14)

    const edge: ContainmentEdge = {
      child: entity.id,
      parent,
      validity: fact.validity,
      via: factRef(entity.id, factKey),
    };
    const bucket = byContext.get(context);
    if (bucket) bucket.push(edge);
    else byContext.set(context, [edge]);
  });

  const out: RawAdvisory[] = [];

  // Iterate contexts in sorted order for deterministic advisory ordering.
  for (const context of [...byContext.keys()].sort()) {
    const edges = byContext.get(context)!;

    // Validity sweep: a loop is only a paradox when its edges are simultaneously
    // live. Any non-empty common-live window of a set of half-open intervals is
    // left-closed at the maximum of their `from` bounds, so sampling at each
    // distinct edge `from` (plus `-∞` for the all-timeless case) is sufficient —
    // a cycle that exists at any instant exists at one of these samples. Cycles
    // are unioned across samples, de-duplicated by their canonical node key.
    const instants = new Set<number>([Number.NEGATIVE_INFINITY]);
    for (const e of edges) instants.add(lowerBound(e.validity));

    const seen = new Set<string>();
    for (const t of [...instants].sort((a, b) => a - b)) {
      const liveEdges: DirectedEdge[] = [];
      const nodes = new Set<string>();
      const viaToValidity = new Map<string, FactValidity | null>();
      for (const e of edges) {
        if (!liveAt(e.validity, t)) continue;
        // The containment relation `(child contained_in parent)` is a directed
        // edge child → parent; a directed cycle is "child is its own ancestor".
        liveEdges.push({ from: e.child, to: e.parent, via: e.via });
        viaToValidity.set(e.via, e.validity);
        nodes.add(e.child);
        nodes.add(e.parent);
      }
      if (liveEdges.length === 0) continue;

      const { cycles } = analyzeDirectedGraph(nodes, liveEdges);
      for (const cycle of cycles) {
        const key = cycle.nodes.join(' ');
        if (seen.has(key)) continue;
        seen.add(key);

        const factIds = cycle.edges.filter((v): v is string => v !== undefined);
        // The fix-or-cancel modal renders the cycle as `A → B → C → A` (§14).
        const path = [...cycle.nodes, cycle.nodes[0]!].join(' → ');

        // Offer the documented one-click fix: close `valid_to` on (drop) the
        // first edge fact of the loop, breaking it. Only a bare predicate slot
        // (no instance suffix) is cleanly removable via the predicate-keyed
        // soft-delete, mirroring `causal.cycle`.
        let fix: RawAdvisory['fix'];
        for (const via of cycle.edges) {
          if (via === undefined) continue;
          const sep = via.indexOf('::');
          if (sep === -1) continue;
          const subject = via.slice(0, sep);
          const factKey = via.slice(sep + 2);
          if (factKey.includes('#')) continue; // instance slot → not predicate-keyed
          fix = {
            kind: 'remove_fact',
            subject,
            factKey,
            label_key: 'auditor.fix.break_cycle',
            params: { length: cycle.nodes.length },
          };
          break;
        }

        out.push({
          severity: 'blocking_error',
          rule: CONTAINMENT_CYCLE_RULE,
          message_key: 'auditor.containment.cycle',
          params: { length: cycle.nodes.length, context, path },
          fact_ids: factIds,
          entity_ids: [...cycle.nodes],
          ...(fix ? { fix } : {}),
        });
      }
    }
  }

  return out;
}
